/**
 * What one person may do in the internal portal, section by section.
 *
 * Three answers per section, in plain words: no access, view only, view and edit.
 * Grouped the way the sidebar is grouped, so "Inventory" is where someone who has just
 * looked at the sidebar expects it, and each group can be set in one click.
 *
 * Two sections have no "edit": the dashboard is a summary and the activity log is
 * append-only by design. Offering "view and edit" there would promise something the
 * system deliberately never does.
 */

import { useI18n, type TranslationKey } from '@/i18n';
import { SECTIONS, type PermissionGrid as Grid, type PermissionLevel, type Section } from '@/lib/api';

export const GROUPS: { key: 'overview' | 'operations' | 'records' | 'compliance' | 'system'; sections: Section[] }[] = [
  { key: 'overview', sections: ['dashboard'] },
  { key: 'operations', sections: ['shifts', 'calendar', 'leave', 'assignments', 'payroll'] },
  { key: 'records', sections: ['employees', 'sites', 'jobs', 'inventory'] },
  { key: 'compliance', sections: ['alerts', 'compliance', 'documents', 'messages'] },
  { key: 'system', sections: ['recycle_bin', 'audit', 'settings', 'users'] },
];

const VIEW_ONLY: ReadonlySet<Section> = new Set<Section>(['dashboard', 'audit']);

export function levelsFor(section: Section): PermissionLevel[] {
  return VIEW_ONLY.has(section) ? ['none', 'read'] : ['none', 'read', 'write'];
}

/** Clamp a level to what the section can express — "edit" on the dashboard is "view". */
function clamp(section: Section, level: PermissionLevel): PermissionLevel {
  return level === 'write' && VIEW_ONLY.has(section) ? 'read' : level;
}

/**
 * Where a new office employee starts: see everything day-to-day, change nothing, and
 * none of the three sections that govern the system.
 *
 * Mirrors seedFor('manager') in hh-backend/src/domain/sections.ts. The server writes
 * that grid itself when none is sent, so a drift here would only change what the form
 * shows before saving — but keep them the same.
 */
export const OFFICE_START: Grid = {
  dashboard: 'read',
  shifts: 'read',
  payroll: 'read',
  assignments: 'read',
  jobs: 'read',
  employees: 'read',
  sites: 'read',
  documents: 'read',
  compliance: 'read',
  alerts: 'read',
  inventory: 'read',
  vehicles: 'read',
  messages: 'read',
  calendar: 'read',
  // Leave requests include sick days: given on purpose, never by default.
  leave: 'none',
  audit: 'none',
  recycle_bin: 'none',
  settings: 'none',
  users: 'none',
};

type Preset = 'viewAll' | 'editDaily' | 'nothing';

function preset(kind: Preset): Grid {
  const grid = {} as Grid;
  for (const section of SECTIONS) {
    const governance = section === 'users' || section === 'settings' || section === 'audit';
    grid[section] =
      kind === 'nothing'
        ? 'none'
        : kind === 'viewAll'
          ? governance
            ? 'none'
            : 'read'
          : governance
            ? 'none'
            : clamp(section, 'write');
  }
  return grid;
}

export function summarise(grid: Grid): { visible: number; editable: number } {
  let visible = 0;
  let editable = 0;
  for (const section of SECTIONS) {
    if (grid[section] !== 'none') visible += 1;
    if (grid[section] === 'write') editable += 1;
  }
  return { visible, editable };
}

export function PermissionGrid({
  value,
  onChange,
  disabled = false,
  inherited,
}: {
  value: Grid;
  onChange: (next: Grid) => void;
  disabled?: boolean;
  /** Sections whose value comes from the role rather than an explicit choice. */
  inherited?: ReadonlySet<Section>;
}) {
  const { t } = useI18n();

  const set = (section: Section, level: PermissionLevel) =>
    onChange({ ...value, [section]: clamp(section, level) });

  const setGroup = (sections: Section[], level: PermissionLevel) => {
    const next = { ...value };
    for (const section of sections) next[section] = clamp(section, level);
    onChange(next);
  };

  const columns: PermissionLevel[] = ['none', 'read', 'write'];
  const grid =
    'grid grid-cols-[minmax(0,1fr)_repeat(3,3.75rem)] sm:grid-cols-[minmax(0,1fr)_repeat(3,5.5rem)] items-center';

  return (
    <div className="space-y-3">
      {/* Starting points, for the common cases. Every row can still be changed after. */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-muted">{t('access.presets.title')}</span>
        {(['viewAll', 'editDaily', 'nothing'] as const).map((kind) => (
          <button
            key={kind}
            type="button"
            disabled={disabled}
            onClick={() => onChange(preset(kind))}
            className="h-7 rounded-full border border-border bg-surface px-3 text-xs font-medium text-muted transition-colors hover:border-[var(--color-primary)] hover:text-primary disabled:opacity-50"
          >
            {t(`access.presets.${kind}` as TranslationKey)}
          </button>
        ))}
      </div>

      {/*
        One matrix, not a stack of segmented controls. The old layout gave every row its
        own three-button strip, so the eye had to find the filled one in each strip, the
        two-option rows were a different width from the three-option ones, and the group
        headers repeated the labels in yet another style. Here the three answers are
        columns, named once, and each row is a single mark in one of them: reading down
        a column answers "who can edit what" at a glance.
      */}
      <div className="overflow-hidden rounded-lg border border-border">
        <div className={`${grid} border-b border-border bg-surface px-3 py-2`}>
          <span />
          {columns.map((level) => (
            <span key={level} className="text-center text-[11px] font-semibold uppercase tracking-wide text-subtle">
              {t(`access.levelShort.${level}` as TranslationKey)}
            </span>
          ))}
        </div>

        {GROUPS.map((group) => {
          const all = (level: PermissionLevel) => group.sections.every((s) => value[s] === clamp(s, level));
          const offered = (level: PermissionLevel) => group.sections.some((s) => levelsFor(s).includes(level));
          return (
            <div key={group.key}>
              <div className={`${grid} border-b border-border bg-surface-alt px-3 py-1.5`}>
                <span className="text-[11px] font-bold uppercase tracking-wider text-subtle">
                  {t(`access.groups.${group.key}` as TranslationKey)}
                </span>
                {columns.map((level) =>
                  group.sections.length > 1 && offered(level) ? (
                    <span key={level} className="flex justify-center">
                      {/* The same mark as the rows, smaller: filled when every section in
                          the group has this level, and a click sets them all to it. */}
                      <Dot
                        small
                        active={all(level)}
                        level={level}
                        disabled={disabled}
                        label={`${t('access.setAll')} ${t(`access.groups.${group.key}` as TranslationKey)}: ${t(`access.level.${level}` as TranslationKey)}`}
                        onClick={() => setGroup(group.sections, level)}
                      />
                    </span>
                  ) : (
                    <span key={level} />
                  ),
                )}
              </div>

              <ul className="divide-y divide-border border-b border-border">
                {group.sections.map((section) => {
                  const allowed = levelsFor(section);
                  const name = t(`access.sections.${section}` as TranslationKey);
                  return (
                    <li key={section} className={`${grid} px-3 py-1`} role="radiogroup" aria-label={name}>
                      <span className="min-w-0 truncate text-sm">
                        {name}
                        {inherited?.has(section) ? (
                          <span className="ms-1.5 text-[11px] text-subtle">{t('access.inherited')}</span>
                        ) : null}
                      </span>
                      {columns.map((level) =>
                        allowed.includes(level) ? (
                          <span key={level} className="flex justify-center">
                            <Dot
                              active={value[section] === level}
                              level={level}
                              disabled={disabled}
                              label={`${name}: ${t(`access.level.${level}` as TranslationKey)}`}
                              onClick={() => set(section, level)}
                            />
                          </span>
                        ) : (
                          // Not "unchecked": this level does not exist for the section.
                          <span key={level} className="text-center text-subtle" aria-hidden="true">
                            {'\u2013'}
                          </span>
                        ),
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The mark in a cell. A ring when not chosen; filled when chosen, in grey for "no
 * access", the brand colour for "view" and green for "edit", so the three columns read
 * differently even to someone not looking at the headers.
 */
function Dot({
  active,
  level,
  disabled,
  label,
  onClick,
  small = false,
}: {
  small?: boolean;
  active: boolean;
  level: PermissionLevel;
  disabled: boolean;
  label: string;
  onClick: () => void;
}) {
  const fill =
    level === 'none'
      ? 'border-subtle bg-subtle'
      : level === 'read'
        ? 'border-primary bg-primary'
        : 'border-success bg-success';
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={`group grid place-items-center rounded-full transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-60 ${
        small ? 'h-7 w-7' : 'h-9 w-9'
      }`}
    >
      <span
        className={`grid place-items-center rounded-full border-2 transition-colors ${small ? 'h-3.5 w-3.5' : 'h-[18px] w-[18px]'} ${
          active ? fill : 'border-border bg-surface group-hover:border-[var(--color-border-strong)]'
        }`}
      >
        {active && !small ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
      </span>
    </button>
  );
}
